import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cn from 'classnames';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';

import { Icon, SlotEditor, SlotSelectionSnapshot } from '@/shared/ui';

import { SectionOptionSchema } from '@/entities/templates';

import { templateEditorMessages } from '../../config';
import { EditorButton } from '../EditorButton';

import { DragHandleGlyph } from './DragHandleGlyph';
import styles from './OptionRow.module.scss';

type OptionRowProps = {
  id: string;
  optionIndex: number;
  displayNumber: string;
  sectionIndex: number;
  option: SectionOptionSchema;
  className?: string;
  isFieldDropTarget?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  insertSlotRef?: React.MutableRefObject<((slotName: string) => void) | null>;
  selectionTextRef?: React.MutableRefObject<SlotSelectionSnapshot | null>;
  onTextChange: (
    sectionIndex: number,
    optionIndex: number,
    text: string,
  ) => void;
  onDelete: (sectionIndex: number, optionIndex: number) => void;
  onOpenAiInstructions: (sectionIndex: number, optionIndex: number) => void;
  onEditorFocus: (sectionIndex: number, optionIndex: number) => void;
  onEditorBlur: () => void;
  onSlotClick?: (
    sectionIndex: number,
    optionIndex: number,
    slotName: string,
  ) => void;
  onSlotCreate?: (
    sectionIndex: number,
    optionIndex: number,
    slotName: string,
    firstValue: string,
  ) => void;
  onEnterPress?: (sectionIndex: number, optionIndex: number) => void;
  onTabPress?: (
    sectionIndex: number,
    optionIndex: number,
    isShiftPressed: boolean,
  ) => void;
};

export const OptionRow: FC<OptionRowProps> = (props) => {
  const {
    id,
    optionIndex,
    displayNumber,
    sectionIndex,
    option,
    className,
    isFieldDropTarget,
    readOnly,
    autoFocus,
    insertSlotRef,
    selectionTextRef,
    onTextChange,
    onDelete,
    onOpenAiInstructions,
    onSlotClick,
    onSlotCreate,
    onEditorFocus,
    onEditorBlur,
    onEnterPress,
    onTabPress,
  } = props;

  const { formatMessage } = useIntl();

  const [isHovered, setIsHovered] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: readOnly,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const hasInstructions = Boolean(option.instructions?.trim());

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-component="template-option-row"
      className={cn(
        styles.option,
        isFieldDropTarget && styles.fieldDropTarget,
        readOnly && styles.readOnly,
        isDragging && styles.dragging,
        className,
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span
        className={cn(
          styles.handle,
          (isHovered || isDragging) && styles.handleVisible,
        )}
        title={formatMessage(templateEditorMessages.dragHandle)}
        {...attributes}
        {...(readOnly ? {} : listeners)}
      >
        <DragHandleGlyph />
      </span>
      <span
        className={cn('p4', styles.number)}
        {...(readOnly ? {} : listeners)}
      >
        {displayNumber}
      </span>

      <SlotEditor
        className={styles.editor}
        value={option.text}
        slots={option.slots}
        slotInstructions={option.slot_instructions}
        placeholder={formatMessage(templateEditorMessages.optionPlaceholder)}
        readOnly={readOnly}
        autoFocus={autoFocus}
        insertSlotRef={insertSlotRef}
        selectionTextRef={selectionTextRef}
        onChange={(text) => onTextChange(sectionIndex, optionIndex, text)}
        onSlotClick={(slotName) =>
          onSlotClick?.(sectionIndex, optionIndex, slotName)
        }
        onSlotCreate={(slotName, firstValue) =>
          onSlotCreate?.(sectionIndex, optionIndex, slotName, firstValue)
        }
        onEditorFocus={() => onEditorFocus(sectionIndex, optionIndex)}
        onEditorBlur={onEditorBlur}
        onEnter={() => onEnterPress?.(sectionIndex, optionIndex)}
        onTab={(isShiftPressed) =>
          onTabPress?.(sectionIndex, optionIndex, isShiftPressed)
        }
      />

      {hasInstructions || (!readOnly && isHovered) ? (
        <EditorButton
          variant="icon"
          disabled={readOnly}
          className={cn(
            styles.aiButton,
            hasInstructions && styles.aiButtonFilled,
          )}
          title={formatMessage(templateEditorMessages.aiInstructionsTitle)}
          onClick={() => onOpenAiInstructions(sectionIndex, optionIndex)}
        >
          <Icon
            name={hasInstructions ? 'comment2Filled' : 'comment2'}
            size={18}
          />
        </EditorButton>
      ) : (
        <span className={styles.iconPlaceholder} />
      )}

      {!readOnly && isHovered ? (
        <EditorButton
          variant="icon"
          className={styles.deleteButton}
          title={formatMessage(templateEditorMessages.deleteOption)}
          onClick={() => onDelete(sectionIndex, optionIndex)}
        >
          <Icon name="delete" size={18} />
        </EditorButton>
      ) : (
        <span className={styles.iconPlaceholder} />
      )}
    </div>
  );
};
